import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CustomerFormFields } from 'page/CustomerPage/types/customerFormField';
import {
  type RegistrationStep,
  REGISTRATION_STEP,
} from 'page/CustomerPage/types/registrationStep';

import type { RootState } from 'store';

import {
  EnvironmentType,
  Channel,
  ENVIRONMENT_TYPES,
  CHANNELS,
} from 'types/customer';

/**
 * 顧客情報の登録画面の状態
 */
interface RegistrationState {
  /**
   * ロード中フラグ
   */
  loading: boolean;
  /**
   * 登録モーダル表示フラグ
   */
  isOpen: boolean;
  /**
   * 登録のステップ
   */
  step: RegistrationStep;
  /**
   * 顧客名
   */
  name: string;
  /**
   * 顧客詳細
   */
  detail: string;
  /**
   * 環境（GCP プロジェクト名）
   */
  environment: string;
  /**
   * 環境種別
   * NOTE: 環境種別は当分は gcp しか使用しない
   */
  environmentType: EnvironmentType;
  /**
   * 顧客番号
   */
  corporateNumber: string;
  /**
   * 契約種別
   */
  channel: Channel;
  /**
   * 顧客環境URL
   */
  poseidonUrl: string;
  /**
   * システム管理上のグループ
   */
  systemUpdateGroup: string;
  /**
   * IAP Client ID
   */
  iapClientId: string;
  /**
   * IAP Client Secret
   */
  iapClientSecret: string;
  /**
   * Poseidon Agent CPU
   */
  poseidonAgentCpu: string;
  /**
   * Poseidon Agent Memory
   */
  poseidonAgentMemory: string;
  /**
   * Enable Vertical Pod Auto Scaler
   */
  enableVerticalPodAutoScaler: boolean;
  /**
   * Zeus トークン
   */
  token: string;
  /**
   * エラーメッセージ
   */
  error: Error | undefined;
}

/**
 * 顧客情報登録画面の初期状態
 */
const initialState: RegistrationState = {
  loading: false,
  isOpen: false,
  step: REGISTRATION_STEP.INPUT,
  name: '',
  detail: '',
  environment: '',
  environmentType: ENVIRONMENT_TYPES.GCP,
  corporateNumber: '',
  channel: CHANNELS.STABLE,
  poseidonUrl: '',
  systemUpdateGroup: '',
  iapClientId: '',
  iapClientSecret: '',
  poseidonAgentCpu: '100m',
  poseidonAgentMemory: '141Mi',
  enableVerticalPodAutoScaler: false,
  token: '',
  error: undefined,
};

/**
 * 顧客情報登録画面の slice(reducer + action)
 */
export const registrationSlice = createSlice({
  name: 'registration',
  initialState,
  reducers: {
    init: () => initialState,
    loadStarted: (state) => {
      state.loading = true;
    },
    loadEnded: (state) => {
      state.loading = false;
    },
    modalOpened: (state) => {
      state.isOpen = true;
    },
    stepMovedToConfirmation: (state) => {
      state.step = REGISTRATION_STEP.CONFIRMATION;
    },
    stepMovedToInput: (state) => {
      state.step = REGISTRATION_STEP.INPUT;
    },
    stepMovedToResult: (state) => {
      state.step = REGISTRATION_STEP.RESULT;
    },
    formGroupUpdated: (
      state,
      action: PayloadAction<{
        form: CustomerFormFields;
      }>,
    ) => {
      state.name = action.payload.form.name;
      state.detail = action.payload.form.detail;
      state.environment = action.payload.form.environment;
      state.environmentType = action.payload.form.environmentType;
      state.corporateNumber = action.payload.form.corporateNumber;
      state.channel = action.payload.form.channel;
      state.poseidonUrl = action.payload.form.poseidonUrl;
      state.systemUpdateGroup = action.payload.form.systemUpdateGroup;
      state.iapClientId = action.payload.form.iapClientId;
      state.iapClientSecret = action.payload.form.iapClientSecret;
      state.poseidonAgentCpu = action.payload.form.poseidonAgentCpu || '';
      state.poseidonAgentMemory = action.payload.form.poseidonAgentMemory || '';
      state.enableVerticalPodAutoScaler =
        action.payload.form.enableVerticalPodAutoScaler || false;
    },
    tokenUpdated: (
      state,
      action: PayloadAction<{
        token: string;
      }>,
    ) => {
      state.token = action.payload.token;
    },
    errorOccured: (
      state,
      action: PayloadAction<{
        error: Error;
      }>,
    ) => {
      state.error = action.payload.error;
    },
  },
});

/**
 * 顧客情報登録画面の action
 */
export const registrationActions = registrationSlice.actions;

/**
 * 顧客情報登録画面の selectors
 */
export const registrationSelectors = {
  loading: (state: RootState) => state.customer.registration.loading,
  isOpenInputModal: (state: RootState) =>
    state.customer.registration.isOpen &&
    state.customer.registration.step === REGISTRATION_STEP.INPUT,
  isOpenConfirmationModal: (state: RootState) =>
    state.customer.registration.isOpen &&
    state.customer.registration.step === REGISTRATION_STEP.CONFIRMATION,
  isOpenResultModal: (state: RootState) =>
    state.customer.registration.isOpen &&
    state.customer.registration.step === REGISTRATION_STEP.RESULT,
  name: (state: RootState) => state.customer.registration.name,
  detail: (state: RootState) => state.customer.registration.detail,
  environment: (state: RootState) => state.customer.registration.environment,
  environmentType: (state: RootState) =>
    state.customer.registration.environmentType,
  corporateNumber: (state: RootState) =>
    state.customer.registration.corporateNumber,
  channel: (state: RootState) => state.customer.registration.channel,
  poseidonUrl: (state: RootState) => state.customer.registration.poseidonUrl,
  systemUpdateGroup: (state: RootState) =>
    state.customer.registration.systemUpdateGroup,
  iapClientId: (state: RootState) => state.customer.registration.iapClientId,
  iapClientSecret: (state: RootState) =>
    state.customer.registration.iapClientSecret,
  poseidonAgentCpu: (state: RootState) =>
    state.customer.registration.poseidonAgentCpu,
  poseidonAgentMemory: (state: RootState) =>
    state.customer.registration.poseidonAgentMemory,
  enableVerticalPodAutoScaler: (state: RootState) =>
    state.customer.registration.enableVerticalPodAutoScaler,
  token: (state: RootState) => state.customer.registration.token,
  hasError: (state: RootState) =>
    state.customer.registration.error !== undefined,
} as const;
